import { useCallback, useRef } from 'react';

/**
 * Guard an async action against overlapping (concurrent) invocations: a call made
 * while a prior one is still in flight is dropped. Use where a handler can be
 * activated twice before its `busy`/`creating` state propagates — a <Form> submit
 * wired to BOTH onSubmitEditing and a <Button submit>, or a fast double-tap on an
 * irreversible action. The in-flight ref is checked and set SYNCHRONOUSLY (before
 * any await), so it wins the same-frame race a useState boolean loses, and clears
 * in `finally`. A dropped call resolves to `undefined`, so a caller that reads the
 * result must treat `undefined` as "did not run".
 *
 * NOT for once-EVER guards (single-use token exchange, one-time redeem/presentation)
 * or animation-lifetime locks — those release on a different signal, not `finally`.
 *
 * Call twice for two independent actions in one component (e.g. save vs. delete).
 */
export function useInFlightGuard() {
  const inFlight = useRef(false);
  return useCallback(async <T>(fn: () => Promise<T>): Promise<T | undefined> => {
    if (inFlight.current) return undefined;
    inFlight.current = true;
    try {
      return await fn();
    } finally {
      inFlight.current = false;
    }
  }, []);
}
